import { getUserRoles } from './keycloak';

/**
 * The realm roles this application knows about.
 *
 * Spelled in lower case with dashes, which is what the realm uses: `admin` came first, and a
 * role name travels as a plain string through places that have no idea it is a role - a
 * `@RolesAllowed` annotation on the service, a comma-separated runtime parameter such as
 * NOTIFICATION_ANNOUNCE_ROLES, a URL. Dashes survive all three and need no quoting anywhere.
 *
 * Not a closed set: the realm may hold roles no release here has heard of, and a token carrying
 * one is not an error. This is only the list of the ones this code makes decisions about.
 */
export const ROLE = {
  /** The super user. Everything below the default, plus the whole Administration section. */
  ADMIN: 'admin',
  /**
   * The default role - what a signed-in user is unless the realm says otherwise. Named here so a
   * check can be written against it, but nothing should *require* it: a user holding only `admin`
   * must not lose the ordinary pages, so the default is "any authenticated user" rather than
   * "holds client-executive". See README-permissions.md.
   */
  CLIENT_EXECUTIVE: 'client-executive'
} as const;

export type Role = typeof ROLE[keyof typeof ROLE];

/**
 * Whether the signed-in user may reach something that requires `required`.
 *
 * No requirement means everybody signed in, which is the default for all but a handful of places
 * in this application - hence `undefined` and `[]` both answering true rather than false. A
 * requirement is satisfied by holding *any* one of the roles listed, not all of them.
 *
 * For deciding what to *offer*, never for deciding what is allowed: the token is a claim the
 * browser cannot verify, so every one of these is enforced again by the service. See
 * hasRealmRole in utils/keycloak.
 */
export const hasAnyRole = (required?: readonly string[]): boolean => {
  if (!required || required.length === 0) return true;
  const held = getUserRoles();
  return required.some((role) => held.includes(role));
};

/** Shorthand for the check written most often. */
export const isAdmin = (): boolean => hasAnyRole([ROLE.ADMIN]);

/**
 * The one role to *show* for the signed-in user, highest first.
 *
 * `admin` wins when both are held, because it is a superset of the other: what the label reports
 * is what the session can do, and saying "client-executive" to somebody holding `admin` would
 * understate it. A token carrying neither is still an ordinary signed-in user - nothing here
 * requires `client-executive` (see README-permissions.md) - so it reads as `client-executive`
 * too, which is the access such a session actually has.
 *
 * For a label, not for a decision: a check belongs in hasAnyRole/isAdmin, which answer about the
 * roles held rather than about the one picked to display.
 */
export const getDisplayRole = (): Role => (isAdmin() ? ROLE.ADMIN : ROLE.CLIENT_EXECUTIVE);
